import React, { useState, useEffect } from 'react';
import { 
  IndianRupee, Megaphone, Users, 
  TrendingUp, ArrowUpRight, Clock, FileText, Loader2
} from 'lucide-react';
// Recharts library for Graph
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, ResponsiveContainer 
} from 'recharts';

const AdminDashboard = () => {
  const [data, setData] = useState<any>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const token = localStorage.getItem("sevaa_admin_token");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // 1. Dashboard ke normal stats fetch karo
       const response = await fetch("https://www.sevaafoundation.com/seva-backend/api/dashboard/stats.php", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();

      // 2. LIVE FIX: Donations fetch karo taaki campaign ka total calculate kar sakein
      const donRes = await fetch("https://www.sevaafoundation.com/seva-backend/api/donations/all.php", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const donData = await donRes.json();
      
      if (result.success && donData.success) {
        // Sirf successful donations filter karo
        const successDonations = donData.donations.filter((d: any) => d.payment_status === 'success');
        
        // Har campaign ka live raised amount calculate karo (DonationOverview ki tarah)
        const updatedCampaigns = result.topCampaigns.map((camp: any) => {
           const liveRaised = successDonations
              .filter((d: any) => d.campaign_name === camp.title)
              .reduce((sum: number, d: any) => sum + Number(d.amount), 0);
              
           return { ...camp, raised_amount: liveRaised }; // Naya live amount set kar diya
        });

        // Purane zero wale data ko naye live data se replace kar do
        result.topCampaigns = updatedCampaigns;

        setData(result);
        processChartData(result.trends);
      } else if (result.success) {
        // Fallback agar donation API fail ho jaye
        setData(result);
        processChartData(result.trends);
      }
    } catch (error) {
      console.error("Error fetching dashboard data", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper: Format Chart Data for Last 7 Days
  const processChartData = (backendTrends: any[]) => {
    const processed = [];
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const dateStr = d.toISOString().split('T')[0];
        
        const existingData = backendTrends.find(t => t.dateStr === dateStr);
        const amount = existingData ? Number(existingData.daily_total) : 0;

        processed.push({
            day: days[d.getDay()],
            amount: amount
        });
    }
    setChartData(processed);
  };

  // Helper: Calculate Time Ago
  const timeAgo = (dateStr: string) => {
    const seconds = Math.floor((new Date().getTime() - new Date(dateStr).getTime()) / 1000);
    let interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " days ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " hours ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " mins ago";
    return "Just now";
  };

  // Helper: Y-Axis formatter for graph (e.g., 1000 -> 1k)
  const formatYAxis = (value: number) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}k`;
    return `₹${value}`;
  };

  if (isLoading) {
    return <div className="h-screen flex items-center justify-center text-orange-500"><Loader2 className="animate-spin w-10 h-10" /></div>;
  }

  if (!data || !data.stats) {
    return (
      <div className="h-screen flex flex-col items-center justify-center text-slate-500">
        <h2 className="text-xl font-bold text-slate-700 mb-2">Oops! Couldn't load dashboard</h2>
        <p className="text-sm mb-4">Make sure your backend server is running.</p>
        <button onClick={() => window.location.reload()} className="px-6 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800">Reload Page</button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up pb-10">
      
      {/* 1. Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Dashboard Overview</h1>
          <p className="text-slate-500 text-sm mt-1">Here's what's happening with Sevaa Foundation today.</p>
        </div>
      </div>

      {/* 2. Colorful Stat Cards (Live Data) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        
        {/* Card 1 - Donations */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow group relative overflow-hidden flex flex-col justify-between">
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-green-500/10 rounded-full group-hover:scale-150 transition-transform duration-500"></div>
          <div className="flex justify-between items-start mb-2">
            <p className="text-slate-500 text-sm font-medium">Total Donations</p>
            <div className="w-10 h-10 bg-green-100 text-green-600 rounded-xl flex items-center justify-center shadow-sm shrink-0">
              <IndianRupee size={20} />
            </div>
          </div>
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-800 truncate pr-2" title={`₹${Number(data.stats.totalDonations).toLocaleString('en-IN')}`}>
              ₹{Number(data.stats.totalDonations).toLocaleString('en-IN')}
            </h3>
            <div className="mt-3 flex items-center text-sm">
              <span className="flex items-center text-green-500 font-semibold bg-green-50 px-2 py-0.5 rounded-md">
                <ArrowUpRight size={16} className="mr-1" /> Live
              </span>
              <span className="text-slate-400 ml-2 text-xs">Total funds</span>
            </div>
          </div>
        </div>

        {/* Card 2 - Campaigns */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow group relative overflow-hidden flex flex-col justify-between">
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-orange-500/10 rounded-full group-hover:scale-150 transition-transform duration-500"></div>
          <div className="flex justify-between items-start mb-2">
            <p className="text-slate-500 text-sm font-medium">Active Campaigns</p>
            <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center shadow-sm shrink-0">
              <Megaphone size={20} />
            </div>
          </div>
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-800 truncate pr-2">{data.stats.activeCampaigns}</h3>
            <div className="mt-3 flex items-center text-sm">
               <span className="flex items-center text-slate-500 font-semibold bg-slate-100 px-2 py-0.5 rounded-md">
                <Clock size={16} className="mr-1" /> Ongoing
              </span>
            </div>
          </div>
        </div>

        {/* Card 3 - Users */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow group relative overflow-hidden flex flex-col justify-between">
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-blue-500/10 rounded-full group-hover:scale-150 transition-transform duration-500"></div>
          <div className="flex justify-between items-start mb-2">
            <p className="text-slate-500 text-sm font-medium">Total Users</p>
            <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center shadow-sm shrink-0">
              <Users size={20} />
            </div>
          </div>
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-800 truncate pr-2">{data.stats.totalUsers}</h3>
            <div className="mt-3 flex items-center text-xs">
              <span className="text-slate-400">Registered staff & admins</span>
            </div>
          </div>
        </div>

        {/* Card 4 - Blogs */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow group relative overflow-hidden flex flex-col justify-between">
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-purple-500/10 rounded-full group-hover:scale-150 transition-transform duration-500"></div>
          <div className="flex justify-between items-start mb-2">
            <p className="text-slate-500 text-sm font-medium">Published Blogs</p>
            <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center shadow-sm shrink-0">
              <FileText size={20} />
            </div>
          </div>
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-800 truncate pr-2">{data.stats.totalBlogs}</h3>
            <div className="mt-3 flex items-center text-sm">
              <span className="flex items-center text-slate-500 font-semibold bg-slate-100 px-2 py-0.5 rounded-md">
                <Clock size={16} className="mr-1" /> Live
              </span>
              <span className="text-slate-400 ml-2 text-xs">Public articles</span>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Charts & Top Campaigns Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Side - REAL GRAPH (Recharts) */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 lg:col-span-2 flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-slate-800">Donation Trends</h3>
            <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200">
              <TrendingUp size={16} className="text-orange-500" /> Last 7 Days
            </div>
          </div>
          
          <div className="h-72 w-full mt-auto">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis 
                  dataKey="day" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 12, fill: '#64748b' }} 
                  dy={10} 
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 12, fill: '#64748b' }} 
                  tickFormatter={formatYAxis}
                />
                <RechartsTooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  formatter={(value: any) => [`₹${value.toLocaleString('en-IN')}`, 'Donations']}
                  labelStyle={{ color: '#64748b', fontWeight: 'bold', marginBottom: '4px' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="amount" 
                  stroke="#f97316" 
                  strokeWidth={3} 
                  fillOpacity={1} 
                  fill="url(#colorAmount)" 
                  activeDot={{ r: 6, fill: '#f97316', stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right Side - Top Campaigns (Live) */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col">
          <h3 className="text-lg font-bold text-slate-800 mb-6">Top Campaigns</h3>
          <div className="space-y-6 flex-1">
            
            {data.topCampaigns.length === 0 && <p className="text-slate-500 text-sm">No campaigns found.</p>}
            
            {data.topCampaigns.map((camp: any, idx: number) => {
               const goal = Number(camp.goal_amount) || 1;
               const raised = Number(camp.raised_amount) || 0;
               const percent = Math.min((raised / goal) * 100, 100);
               const colors = ['bg-orange-500', 'bg-blue-500', 'bg-green-500'];
               const textColors = ['text-orange-600', 'text-blue-600', 'text-green-600'];

               return (
                <div key={idx}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-semibold text-slate-700 truncate pr-2">{camp.title}</span>
                    <span className={`${textColors[idx]} font-bold`}>{Math.round(percent)}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5">
                    <div className={`${colors[idx]} h-2.5 rounded-full transition-all duration-1000`} style={{ width: `${percent}%` }}></div>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">₹{raised.toLocaleString('en-IN')} raised of ₹{goal.toLocaleString('en-IN')} goal</p>
                </div>
               );
            })}

          </div>
          <a href="/n-jio-sevaa/campaigns/live" className="w-full mt-6 py-2.5 border border-slate-200 text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-colors text-sm text-center block">
            View All Campaigns
          </a>
        </div>
      </div>

      {/* 4. Recent Transactions Table (Live) */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h3 className="text-lg font-bold text-slate-800">Recent Donations</h3>
          <a href="/n-jio-sevaa/donations/overview" className="text-orange-600 text-sm font-semibold hover:text-orange-700">See all</a>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-slate-50 text-slate-500 uppercase text-xs font-semibold">
              <tr>
                <th className="px-6 py-4">ID</th>
                <th className="px-6 py-4">Donor Name</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.recentDonations.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-slate-500">No recent donations.</td></tr>
              )}
              {data.recentDonations.map((donation: any) => (
                <tr key={donation.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-slate-900">#{donation.id}</td>
                  <td className="px-6 py-4 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold text-xs uppercase shrink-0">
                      {donation.name.charAt(0)}
                    </div>
                    <span className="truncate max-w-[150px] block">{donation.name}</span>
                  </td>
                  <td className="px-6 py-4 font-bold text-slate-800">₹{Number(donation.amount).toLocaleString('en-IN')}</td>
                  <td className="px-6 py-4 text-slate-400 whitespace-nowrap">{timeAgo(donation.created_at)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                      donation.payment_status === 'success' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {donation.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default AdminDashboard;